/**
 * HederaStakingWidget Component
 *
 * Compact widget showing Hedera HBAR staking status for the Portfolio Dashboard
 * - Shows total balance and pending staking rewards
 * - Clickable to navigate to HederaMonitor page
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { useHederaWallets } from '../../hooks/useHederaWallet';
import { MONITOR_SYNC_EVENT, STORAGE_KEYS } from '../../hooks/useMonitorWalletsSync';

const ACCOUNTS_STORAGE_KEY = STORAGE_KEYS.hedera.wallets;

interface HederaStakingWidgetProps {
  onNavigate?: (pageId: string) => void;
}

export function HederaStakingWidget({ onNavigate }: HederaStakingWidgetProps) {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<string[]>([]);

  // Load accounts from localStorage
  const loadAccounts = useCallback(() => {
    const savedAccounts = localStorage.getItem(ACCOUNTS_STORAGE_KEY);
    if (savedAccounts) {
      try {
        const parsed = JSON.parse(savedAccounts);
        if (Array.isArray(parsed)) {
          setAccounts(parsed);
        }
      } catch (e) {
        console.error('Error loading Hedera accounts:', e);
      }
    }
  }, []);

  // Load accounts on mount and when sync completes
  useEffect(() => {
    loadAccounts();

    // Listen for monitor sync events to reload accounts
    const handleSyncComplete = () => {
      console.log('[HederaWidget] Sync event received, reloading accounts...');
      loadAccounts();
    };

    window.addEventListener(MONITOR_SYNC_EVENT, handleSyncComplete);
    return () => {
      window.removeEventListener(MONITOR_SYNC_EVENT, handleSyncComplete);
    };
  }, [loadAccounts]);

  // Fetch wallet data
  const {
    totalBalance,
    totalPendingRewards,
    isLoading,
  } = useHederaWallets(accounts);

  const handleClick = () => {
    if (onNavigate) {
      onNavigate('hedera-monitor');
    }
  };

  // Format HBAR amounts
  const formatHBAR = (amount: number) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(2)}M`;
    }
    if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    }
    return amount.toFixed(2);
  };

  const hasRewards = totalPendingRewards > 0;
  const isConfigured = accounts.length > 0;

  // Show skeleton/placeholder if not configured
  if (!isConfigured) {
    return (
      <div
        onClick={handleClick}
        style={{
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.06), rgba(99, 102, 241, 0.04))',
          borderRadius: '12px',
          padding: '12px 16px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          border: '1px dashed rgba(139, 92, 246, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '200px',
          opacity: 0.7,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.7';
          e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.3)';
        }}
        title={t('hedera.widget.clickToConfigure', 'Cliquer pour configurer Hedera Staking')}
      >
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: 'rgba(139, 92, 246, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icons.Hedera size={18} color="rgba(0, 0, 0, 0.5)" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)', opacity: 0.6 }}>
              HBAR Staking
            </span>
          </div>
          <span style={{ fontSize: '11px', color: 'var(--muted)', opacity: 0.6 }}>
            {t('hedera.widget.clickToConfigure', 'Cliquer pour configurer')}
          </span>
        </div>
        <Icons.Plus size={16} style={{ color: 'rgba(139, 92, 246, 0.5)', flexShrink: 0 }} />
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      style={{
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12), rgba(99, 102, 241, 0.08))',
        borderRadius: '12px',
        padding: '12px 16px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: '1px solid rgba(139, 92, 246, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        minWidth: '200px',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.2)';
        e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.4)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.2)';
      }}
      title={t('hedera.widget.clickToOpen', 'Cliquer pour ouvrir Hedera Monitor')}
    >
      {/* Icon */}
      <div
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icons.Hedera size={18} color="white" />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>
            HBAR Staking
          </span>
          {/* Status indicator */}
          {isLoading ? (
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#6b7280',
                animation: 'pulse 1s infinite',
              }}
            />
          ) : totalBalance > 0 ? (
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: hasRewards ? '#10b981' : '#8b5cf6',
                boxShadow: hasRewards ? '0 0 8px #10b981' : '0 0 8px #8b5cf6',
              }}
            />
          ) : null}
        </div>

        {/* Balance and rewards info */}
        {isLoading ? (
          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
            Chargement...
          </span>
        ) : totalBalance > 0 ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Icons.Wallet size={10} style={{ color: '#8b5cf6' }} />
              <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                {formatHBAR(totalBalance)} HBAR
              </span>
            </div>
            {totalPendingRewards > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Icons.Gift size={10} style={{ color: '#10b981' }} />
                <span style={{ fontSize: '11px', color: '#10b981' }}>
                  +{totalPendingRewards.toFixed(2)}
                </span>
              </div>
            )}
          </div>
        ) : (
          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
            {t('hedera.widget.noBalance', 'Aucun solde')}
          </span>
        )}
      </div>

      {/* Arrow */}
      <Icons.ChevronRight size={16} style={{ color: 'var(--muted)', flexShrink: 0 }} />

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
